// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
// Style imports:
// import './AlertConfig.scss';
// Image imports:
// import CHFflag from '../../assets/flags/CHFflag.svg';
// import DKKflag from '../../assets/flags/DKKflag.svg';
// import EURflag from '../../assets/flags/EURflag.svg';
// import GBPflag from '../../assets/flags/GBPflag.svg';
// import ISKflag from '../../assets/flags/ISKflag.svg';
// import JPYflag from '../../assets/flags/JPYflag.svg';
// import NOKflag from '../../assets/flags/NOKflag.svg';
// import SEKflag from '../../assets/flags/SEKflag.svg';
// import USDflag from '../../assets/flags/USDflag.svg';
// Component imports:
import ConfigurationModal from './ConfigurationModal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import StickyHeaderTable from '../common/StickyHeaderTable';
import SortFilterTable from '../common/SortFilterTable';
// Service imports:
import { isParameterString } from '../../services/alertUtils';
// Action imports:
import { requestAlertsConfig, modifyAlertConfigs } from '../../redux-store/actions/alertActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { IAlertConfig, IAlertConfigParameter, IAlertConfigParameterChange, IAlertConfigParameterHistoryEntry } from '../../types/api/AlertTypes';

// const flagMap: {
//     [key: string]: string
// } = {
//     'CHF': CHFflag,
//     'DKK': DKKflag,
//     'EUR': EURflag,
//     'GBP': GBPflag,
//     'ISK': ISKflag,
//     'JPY': JPYflag,
//     'NOK': NOKflag,
//     'SEK': SEKflag,
//     'USD': USDflag
// }

const findMostRecentHistoryEntry = (alertParameter: IAlertConfigParameter) => {
    const { alertConfigParameterHistory } = alertParameter;
    const mostRecentActiveParameterHistoryEntry = alertConfigParameterHistory.reduce((mostRecentSoFar: IAlertConfigParameterHistoryEntry | undefined, currentHistoryEntry: IAlertConfigParameterHistoryEntry) => {
        if (mostRecentSoFar === undefined) return currentHistoryEntry;
        if (new Date(mostRecentSoFar.created_timestamp) > new Date(currentHistoryEntry.created_timestamp)) return mostRecentSoFar;
        return currentHistoryEntry;
    }, undefined);
    return mostRecentActiveParameterHistoryEntry;
}

type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps;


const AlertConfig: React.FC<Props> = ({
    alertConfigs,
    accessTokenV2,
    requestAlertsConfig,
    modifyAlertConfigs
}) => {
    useEffect(() => {
        document.title = 'Alert Configuration | KODIAK Sigti';
    }, []);

    const [ selectedAlert, setSelectedAlert ] = useState<IAlertConfig | null>(null);
    
    useEffect(() => {
        if (alertConfigs === undefined) {
            requestAlertsConfig(accessTokenV2);
        }
    }, [ alertConfigs, accessTokenV2, requestAlertsConfig ]);

    const clickActiveButton = (alert: IAlertConfig, newActiveValue: number) => {
        const newChanges: IAlertConfigParameterChange[] = []
        for (let parameter of alert.alertConfigParameters) {
            if (parameter.id === 'active') {
                const newAlertConfigParameterChange: IAlertConfigParameterChange = {
                    alertId: alert.id,
                    paramId: 'active',
                    newValue: newActiveValue
                };
                newChanges.push(newAlertConfigParameterChange);
            } else {
                // If there are default values, include them in the request.
                const mostRecentHistoryEntry = findMostRecentHistoryEntry(parameter);
                if (mostRecentHistoryEntry === undefined) {
                    const newAlertConfigParameterChange: IAlertConfigParameterChange = {
                        alertId: parameter.alert_id,
                        paramId: parameter.id,
                        newValue: (isParameterString(parameter.id))
                            ? parameter.default_value_text
                            : parameter.default_value
                    };
                    newChanges.push(newAlertConfigParameterChange);
                }
            }
        }
        modifyAlertConfigs(accessTokenV2, newChanges);
    }

    const booleanToIcon = (value: boolean | null) => (value) ? (
        <FontAwesomeIcon
            className='GreenIcon'
            icon={faCheck}
        />
    ) : (
        <FontAwesomeIcon
            className='RedIcon'
            icon={faTimes}
        />
    );

    const { alertConfigList, isLoading, isError } = useMemo(() => {
        const data: {
            alertConfigList: IAlertConfig[],
            isLoading: boolean,
            isError: boolean
        } = {
            alertConfigList: [],
            isLoading: false,
            isError: false
        }

        if (alertConfigs === 'waiting' || alertConfigs === undefined) {
            data.isLoading = true;
            return data;
        }
        if (alertConfigs instanceof Error) {
            data.isError = true;
            return data;
        }
        
        data.alertConfigList = Array.isArray(alertConfigs) ? [...alertConfigs].sort((config1, config2) => {
            if (config1.id < config2.id) return -1;
            if (config1.id > config2.id) return 1;
            return 0;
        }) : [];
        return data;
    }, [ alertConfigs ]);

    const AlertConfigButtonRenderer = (alert: IAlertConfig) => {
        return (
            <Button
                variant='dark'
                className='VerySmallButton'
                onClick={() => setSelectedAlert(alert)}
            >
                Configure
            </Button>
        )
    }

    const AlertConfigActiveButtonRenderer = ({ alert, isActive }: { alert: IAlertConfig, isActive: boolean }) => {
        return (
            <td className='OnOffButtonTd'>
                {
                    (isActive) ? (
                        <div
                            className='ToggleBackground ToggleOn'
                            onClick={() => clickActiveButton(alert, 0)}
                        >
                            <div className='ToggleButton' />
                        </div>
                    ) : (
                        <div
                            className='ToggleBackground ToggleOff'
                            onClick={() => clickActiveButton(alert, 1)}
                        >
                            <div className='ToggleButton' />
                        </div>
                    )
                }
            </td>
        )
    }

    return (
        <Container fluid id='AlertConfig'>
            <Row>
                <Col xs={6}>
                    <h5 className='SectionTitle'>Alert Configuration</h5>
                </Col>
                <Col xs={6} className='text-right'>
                    <Button
                        as='a'
                        variant='dark'
                        className='DocumentationButton'
                        rel='noopener noreferrer'
                        target='_blank'
                        href='https://kodihq.atlassian.net/wiki/spaces/SIGTI/pages/575406107/SIGTI+Alerts'
                    >
                        See documentation
                    </Button>
                </Col>
            </Row>
            <div className='TableWrapper'>
                <StickyHeaderTable
                    hideScrollbar={false}
                >
                    <SortFilterTable
                        data={alertConfigList}
                        isLoading={isLoading}
                        isError={isError}
                        columns={[{
                            title: 'Alert ID',
                            dataAccessor: ({id}) => id,
                            textAlign: 'left',
                            simpleSortable: ({id}) => id
                        }, {
                            title: 'Description',
                            dataAccessor: ({description}) => description,
                            textAlign: 'left',
                            simpleSortable: ({description}) => description
                        }, {
                            title: 'Details',
                            dataAccessor: ({description_long}) => description_long,
                            textAlign: 'left',
                            simpleSortable: ({description_long}) => description_long
                        }, {
                            title: 'Private',
                            dataAccessor: ({private: priv}) => booleanToIcon(priv),
                            textAlign: 'center',
                            simpleSortable: ({private: priv}) => priv ? 1 : 0,
                            sortTiebreaker: (a, b) => a.id - b.id
                        }, {
                            title: 'Shares',
                            dataAccessor: ({shares}) => booleanToIcon(shares),
                            textAlign: 'center',
                            simpleSortable: ({shares}) => shares ? 1 : 0,
                            sortTiebreaker: (a, b) => a.id - b.id
                        }, {
                            title: 'Bonds',
                            dataAccessor: ({bonds}) => booleanToIcon(bonds),
                            textAlign: 'center',
                            simpleSortable: ({bonds}) => bonds ? 1 : 0,
                            sortTiebreaker: (a, b) => a.id - b.id
                        }, {
                            title: 'Currency',
                            dataAccessor: ({currency}) => currency,
                            textAlign: 'center',
                            simpleSortable: ({currency}) => currency,
                            sortTiebreaker: (a, b) => a.id - b.id,
                        }, {
                            title: '',
                            dataAccessor: (alert) => (
                                <AlertConfigButtonRenderer {...alert} />
                            ),
                        }, {
                            title: 'Active',
                            dataAccessor: (alert) => {
                                const { alertConfigParameters } = alert;
                                const activeParameter = alertConfigParameters.find(parameter => parameter.id === 'active');
                                const mostRecentActiveParameterHistoryEntry = (activeParameter === undefined)
                                    ? undefined
                                    : findMostRecentHistoryEntry(activeParameter);
                                const isActive = (mostRecentActiveParameterHistoryEntry === undefined) ? false : (mostRecentActiveParameterHistoryEntry.value === 1);
                                return (
                                    <AlertConfigActiveButtonRenderer alert={alert} isActive={isActive} />
                                )
                            },
                            overrideTd: true
                        }]}
                    />
                </StickyHeaderTable>
            </div>
            {
                (selectedAlert !== null)
                ? (
                    <ConfigurationModal
                        closeModal={() => setSelectedAlert(null)}
                        alertConfiguration={selectedAlert}
                    />
                ) : null
            }
        </Container>
    );
}

function mapStateToProps({ alertReducer, loginReducer }: RootState) {
    const { alertConfigs } = alertReducer;
    const { accessTokenV2 } = loginReducer;
    return {
        alertConfigs,
        accessTokenV2
    };
}

const connector = connect(mapStateToProps, { requestAlertsConfig, modifyAlertConfigs });
export default connector(AlertConfig);
